import {
  fetchBlob,
  getProductionPlanControllerGetWorkOrderPlanQueryOptions,
  productionPlanControllerApplyReschedule,
  productionPlanControllerGetSummary,
  productionPlanControllerGetWorkOrderAtRiskReasons,
  productionPlanControllerPreviewReschedule,
  setRouteControllerSetRoute,
  throughputControllerGetPlannedThroughput,
  workOrdersPartnerControllerListJobCards,
  workOrdersPartnerControllerReorderManufacturingSequence,
} from '@artic/api-client';
import type {
  JobCardRowDto,
  PlannedThroughputResponseDto,
  ProductionPlanSummaryResponseDto,
  ReorderManufacturingSequenceDto,
  ReorderManufacturingSequenceResponseDto,
  RescheduleApplyRequestDto,
  ReschedulePreviewRequestDto,
  RescheduleResponseDto,
  SetRouteRequestDto,
  WorkOrderAtRiskReasonsResponseDto,
  WorkOrderPlanResponseDto,
  SetRouteResponseDto,
} from '@artic/api-client';
import { ApiError } from '@artic/api-client';
import * as Sentry from '@sentry/react';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { PROJECT_PLANNING_KEY } from '../project-planning/queries';
import { clientIdHeaders } from '../realtime/client-id';
import { THROUGHPUT_CAPACITY_KEY } from './capacity-view/queries';
import {
  applyProductionPlannerPageCacheUpdate,
  mapProductionPlannerRows,
  type ProductionPlannerPageData,
} from './production-planner-page-cache';

export const PRODUCTION_PLANNER_KEY = 'production-planner' as const;

export function workOrderPlanQueryKey(
  partnerId: string | undefined,
  workOrderId: string | undefined,
) {
  return [PRODUCTION_PLANNER_KEY, 'plan', partnerId, workOrderId] as const;
}

/**
 * Live Plan detail for one work order. Disabled until the Plan cell opens so
 * rendering a planner page never creates one request per visible row.
 */
export function workOrderPlanQueryOptions(
  partnerId: string | undefined,
  workOrderId: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: workOrderPlanQueryKey(partnerId, workOrderId),
    queryFn: async ({ signal, client, meta }) => {
      if (!partnerId || !workOrderId) throw new Error('partnerId and workOrderId are required');
      const generatedOptions = getProductionPlanControllerGetWorkOrderPlanQueryOptions(
        partnerId,
        workOrderId,
      );
      if (typeof generatedOptions.queryFn !== 'function') {
        throw new Error('Work order Plan query is unavailable');
      }
      const res = await generatedOptions.queryFn({
        client,
        meta,
        queryKey: generatedOptions.queryKey,
        signal,
      });
      return expectData<WorkOrderPlanResponseDto>(res);
    },
    enabled: enabled && !!partnerId && !!workOrderId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Job cards for a single work order — fetched lazily by the master/detail
 * child renderer when the user expands a row. The endpoint URL keeps the
 * `work-orders` segment because the domain concept (a work order's job
 * cards) is unaffected by the route rename.
 */
export function workOrderJobCardsQueryOptions(workOrderId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTION_PLANNER_KEY, 'job-cards', workOrderId] as const,
    queryFn: async ({ signal }) => {
      if (!workOrderId) throw new Error('workOrderId is required');
      const res = await workOrdersPartnerControllerListJobCards(workOrderId, { signal });
      return res.data as JobCardRowDto[];
    },
    enabled: !!workOrderId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Why one work order is at risk — fetched on demand when the user opens the At
 * Risk fan-out, never per visible row. Nested under `PRODUCTION_PLANNER_KEY` so
 * the planner's existing prefix invalidations (resequence, reschedule, route
 * changes, the capacity realtime ping) already reach it.
 */
export function workOrderAtRiskReasonQueryOptions(
  partnerId: string | undefined,
  workOrderId: string | undefined,
) {
  return queryOptions({
    queryKey: [PRODUCTION_PLANNER_KEY, 'at-risk-reason', partnerId, workOrderId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId || !workOrderId) throw new Error('partnerId and workOrderId are required');
      const res = await productionPlanControllerGetWorkOrderAtRiskReasons(partnerId, workOrderId, {
        signal,
      });
      return expectData<WorkOrderAtRiskReasonsResponseDto>(res);
    },
    enabled: !!partnerId && !!workOrderId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Reorders selected work orders within their factory and updates affected sequence numbers.
 *
 * @returns A mutation for applying manufacturing sequence changes.
 */
export function useReorderManufacturingSequence(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: ReorderManufacturingSequenceDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await workOrdersPartnerControllerReorderManufacturingSequence(
        partnerId,
        params,
        clientIdHeaders(),
      );
      return res.data as ReorderManufacturingSequenceResponseDto;
    },
    onSuccess: (data) => {
      if (data.sequence_changes.length === 0) return;
      const changeById = new Map(data.sequence_changes.map((c) => [c.work_order_id, c.to]));
      queryClient.setQueriesData<ProductionPlannerPageData>(
        { queryKey: [PRODUCTION_PLANNER_KEY, 'partner', partnerId, 'pages'] },
        (old) =>
          applyProductionPlannerPageCacheUpdate(old, (cache) =>
            mapProductionPlannerRows(cache, (row) => {
              const to = changeById.get(row.work_order_id);
              return to === undefined ? row : { ...row, work_order_sequence_number: to };
            }),
          ),
      );
    },
    onError: (err, vars) => {
      // The user only sees a toast; without this, a staging/prod failure
      // ("Validation failed", 404 anchor, …) leaves nothing to diagnose.
      // Capture the request shape and the response body alongside the error.
      Sentry.captureException(err, {
        tags: { feature: 'planner-reorder-sequence' },
        contexts: {
          reorder_request: {
            mode: vars.mode,
            work_order_count: vars.work_order_ids.length,
            anchor_work_order_id: vars.anchor_work_order_id ?? null,
            anchor_work_order_name: vars.anchor_work_order_name ?? null,
            target_sequence_number: vars.target_sequence_number ?? null,
          },
          ...(err instanceof ApiError
            ? {
                reorder_response: {
                  status: err.status,
                  body: JSON.stringify(err.body)?.slice(0, 2000) ?? null,
                },
              }
            : {}),
        },
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail'] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
    },
  });
}

/**
 * Download scan labels (a PDF, one 200mm × 63mm label per work order) for the
 * selected work orders (Production Planner action). Returns the PDF Blob so
 * the caller can trigger the browser download. Read-only on the server — no
 * cache invalidation. Uses `fetchBlob` because the response is binary and
 * `customFetch` always JSON-parses the body.
 */
export function useDownloadScanLabels(partnerId: string | undefined) {
  return useMutation({
    mutationFn: async (work_order_ids: string[]) => {
      if (!partnerId) throw new Error('partnerId is required');
      return fetchBlob(`/partners/${partnerId}/work-order-labels`, {
        method: 'POST',
        body: JSON.stringify({ work_order_ids }),
      });
    },
  });
}

// ── ENG-670 Production Plan summary + throughput ─────────────────────

export function productionPlanSummaryQueryKey(
  partnerId: string | undefined,
  factoryId: string | null,
  from: string,
  to: string,
  routeIds: readonly string[] | null,
) {
  // Sorted + joined so the key is order-insensitive: the grid's set filter hands
  // its values back in whatever order the user ticked them, and two identical
  // selections must not be two cache entries.
  const routeKey = routeIds === null ? null : [...routeIds].sort().join(',');
  return [PRODUCTION_PLANNER_KEY, 'summary', partnerId, factoryId, from, to, routeKey] as const;
}

/**
 * Summary-strip figures for the visible period ([from, to] inclusive
 * yyyy-MM-dd). `factoryId: null` = every partner factory.
 *
 * `routeIds` is the Route COLUMN FILTER's selection (ENG-876), and it scopes
 * the three throughput figures only — the capacity checks stay factory-level,
 * as ENG-875 requires. `null` = no route filter applied.
 */
export function productionPlanSummaryQueryOptions(
  partnerId: string | undefined,
  factoryId: string | null,
  from: string,
  to: string,
  routeIds: readonly string[] | null = null,
) {
  return queryOptions({
    queryKey: productionPlanSummaryQueryKey(partnerId, factoryId, from, to, routeIds),
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await productionPlanControllerGetSummary(
        partnerId,
        {
          from,
          to,
          ...(factoryId ? { factory_id: factoryId } : {}),
          ...(routeIds && routeIds.length > 0 ? { route_ids: [...routeIds] } : {}),
        },
        { signal },
      );
      return expectData<ProductionPlanSummaryResponseDto>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Weekly Planned Throughput targets for one factory over [from, to]. Also the
 * planner's `week_starts_on` signal: returned `week_commencing` dates fall on
 * the partner's first day of week, so the grid view self-corrects its
 * Monday-default week bucketing from the first row (mirrors the Capacity
 * Plan's payload-driven bootstrap).
 */
export function plannedThroughputQueryOptions(factoryId: string | null, from: string, to: string) {
  return queryOptions({
    queryKey: [PRODUCTION_PLANNER_KEY, 'planned-throughput', factoryId, from, to] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await throughputControllerGetPlannedThroughput(
        factoryId,
        { from, to },
        { signal },
      );
      return expectData<PlannedThroughputResponseDto>(res);
    },
    enabled: !!factoryId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ── ENG-670 Reschedule (block move) ──────────────────────────────────

/**
 * Dry-run of a block-move reschedule — no writes. Powers the dialog's
 * calendar markers and sequence-impact preview, so no cache invalidation.
 */
export function usePreviewReschedule(partnerId: string | undefined) {
  return useMutation({
    mutationFn: async (dto: ReschedulePreviewRequestDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await productionPlanControllerPreviewReschedule(partnerId, dto);
      return expectData<RescheduleResponseDto>(res);
    },
  });
}

/**
 * Apply a block-move reschedule. Invalidates the production planner AND the
 * project planning grids (mirrors useReorderManufacturingSequence — both
 * surfaces show the moved dates and Manufacturing Sequence Numbers).
 */
export function useApplyReschedule(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: RescheduleApplyRequestDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await productionPlanControllerApplyReschedule(partnerId, dto);
      return expectData<RescheduleResponseDto>(res);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail'] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
    },
  });
}

/**
 * ENG-743 §17: the bulk Set Route endpoint — the only user-facing route
 * write. A reroute moves the work orders' FACTORY too, so beyond the two
 * planning grids it invalidates the capacity view (demand shifts between
 * factory rows). No optimistic cache write — the dialog closes on success
 * and the invalidated planner query repaints within one fetch.
 */
export function useSetWorkOrderRoutes(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: SetRouteRequestDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await setRouteControllerSetRoute(partnerId, dto);
      return expectData<SetRouteResponseDto>(res);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail'] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [THROUGHPUT_CAPACITY_KEY] });
    },
  });
}
